import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, ArrowLeft } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { KIND_LABEL, pickText } from "@/lib/recipe";
import { RecipeMeta, RecipeBody, VideoRecipeLink } from "@/components/RecipeDialog";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function PrintRecipe() {
  const { code, kind } = useParams();
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const country = countries.find((c) => c.code === code?.toUpperCase());
  const food = country?.travel_guide?.cuisine?.[kind];
  const name = food ? pickText(food.name, lang) : "";

  useEffect(() => {
    if (food) document.title = `${name} — ${t("recipe")}`;
    return () => { document.title = "Atlante del Mondo Arabo | أطلس العالم العربي"; };
  }, [food, name, t]);

  if (!countries.length) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  if (!food?.recipe) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground" data-testid="print-recipe-missing">404</div>;

  const countryName = country.names[lang] || country.names.en;
  const onPrint = () => { trackEvent("print_recipe", { country_code: country.code, kind, lang }); window.print(); };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 print:py-0 print:px-0 print:max-w-none" data-testid="print-recipe">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to={`/?c=${country.code}`} className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="print-recipe-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}
        </Link>
        <div className="flex gap-2">
          <VideoRecipeLink food={food} code={country.code} kind={kind} className="text-sm px-4 py-2" />
          <button onClick={onPrint} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-sand text-sm hover:bg-ochre transition-colors" data-testid="print-recipe-button">
            <Printer className="w-4 h-4" /> {t("print_pdf")}
          </button>
        </div>
      </div>

      <header className="flex items-center gap-5 border-b-2 border-ochre pb-5 mb-6">
        <img src={country.flag_url} alt="" className="w-20 h-[54px] object-cover rounded-md border border-stoneLine shadow-sm" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold">{t("app_title")} · {countryName} · {t(KIND_LABEL[kind])}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight" data-testid="print-recipe-title">{name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{pickText(food.desc, lang)}</p>
        </div>
      </header>

      {food.image && <img src={imgSrc(food.image)} alt={name} className="w-full h-56 object-cover rounded-xl border border-stoneLine mb-5 break-inside-avoid" />}
      <div className="mb-5"><RecipeMeta recipe={food.recipe} size="text-sm" /></div>
      <RecipeBody recipe={food.recipe} lang={lang} />

      <footer className="mt-8 pt-3 border-t border-stoneLine text-[10px] text-muted-foreground flex justify-between">
        <span>{t("photo_wiki")}</span>
        <span dir="ltr">{window.location.origin}/recipe/{country.code}/{kind}</span>
      </footer>
    </div>
  );
}
